import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import TrialBanner from './TrialBanner';
import LanguageSwitcher from './LanguageSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Ticket,
  Users,
  Bot,
  LogOut,
  Headphones,
  PhoneCall,
  Shield,
  Monitor,
  BookOpen,
  Menu,
  X,
  Plus,
  Bell,
  Search,
  Globe
} from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navGroups = [
    {
      label: 'OPERATIVO',
      items: [
        { path: '/', label: t('dashboard'), icon: LayoutDashboard },
        { path: '/tickets', label: t('tickets'), icon: Ticket },
        { path: '/assets', label: t('assets'), icon: Monitor },
        { path: '/kb', label: t('kb'), icon: BookOpen },
        { path: '/technicians', label: t('technicians'), icon: Users },
      ]
    },
    {
      label: 'CANALI',
      items: [
        { path: '/ai-chat', label: t('aiChat'), icon: Bot, live: true },
        { path: '/voice-call', label: t('voice_call_nav'), icon: PhoneCall },
      ]
    }
  ];

  if (user?.role === 'super_admin') {
    navGroups.push({
      label: 'AMMINISTRAZIONE',
      items: [{ path: '/admin', label: t('super_admin_nav'), icon: Shield }]
    });
  }

  const allItems = navGroups.flatMap(g => g.items);
  const bottomNavItems = allItems.slice(0, 5);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/' && location.pathname.startsWith(path));

  const titles = {
    '/': [t('page_dashboard_title'), t('page_dashboard_subtitle')],
    '/tickets': [t('page_tickets_title'), t('page_tickets_subtitle')],
    '/assets': [t('page_assets_title'), t('page_assets_subtitle')],
    '/kb': [t('page_kb_title'), t('page_kb_subtitle')],
    '/technicians': [t('page_technicians_title'), t('page_technicians_subtitle')],
    '/admin': [t('page_admin_title'), t('page_admin_subtitle')],
    '/ai-chat': [t('page_ai_chat_title'), t('page_ai_chat_subtitle')],
    '/voice-call': [t('page_voice_call_title'), t('page_voice_call_subtitle')],
    '/upgrade': [t('page_upgrade_title'), t('page_upgrade_subtitle')],
  };
  const currentBase = '/' + location.pathname.split('/')[1];
  const [pageTitle, pageSub] = titles[currentBase] || titles['/'];

  const SidebarNav = ({ onNav }) => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-2 pb-5 pt-1.5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'linear-gradient(150deg, #3B6FE0, #2A52C4)', boxShadow: '0 6px 16px rgba(43,82,196,.45)' }}>
          <Headphones className="w-[22px] h-[22px] text-white" />
        </div>
        <div>
          <div className="font-extrabold text-base text-white" style={{ letterSpacing: '-0.2px' }}>IT Helpdesk</div>
          <div className="text-[11px] font-semibold" style={{ color: '#7E8DA8', letterSpacing: '0.3px' }}>IKONET Solutions</div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-0.5 overflow-y-auto">
        {navGroups.map((group) => (
          <div key={group.label}>
            <div className="px-2.5 pt-4 pb-2 text-[10.5px] font-bold" style={{ letterSpacing: '1.4px', color: '#5E6F8E' }}>
              {group.label}
            </div>
            <div className="space-y-[3px]">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link key={item.path} to={item.path} onClick={onNav}
                    data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm font-semibold transition-all"
                    style={active
                      ? { background: '#2A52C4', color: '#fff', boxShadow: '0 6px 16px rgba(42,82,196,.4)' }
                      : { background: 'transparent', color: '#9AA8C2' }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#1A2741'; e.currentTarget.style.color = '#D6E0F2'; }}}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9AA8C2'; }}}>
                    <Icon className="w-[19px] h-[19px] shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.live && <span className="w-[7px] h-[7px] rounded-full" style={{ background: '#34C759', animation: 'live-blink 1.8s infinite' }} />}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User card */}
      <div className="flex items-center gap-2.5 p-2.5 rounded-[14px] mt-auto" style={{ background: '#1A2741' }}>
        <div className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center font-bold text-[15px] text-white shrink-0"
          style={{ background: 'linear-gradient(150deg, #3B6FE0, #2A52C4)' }}>
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-bold text-white truncate">{user?.name}</div>
          <div className="text-[11px] font-semibold" style={{ color: '#7E8DA8' }}>
            {user?.role === 'super_admin' ? t('role_super_admin') : user?.role === 'admin' ? t('role_admin') : t('role_technician')}
          </div>
        </div>
        <button onClick={handleLogout} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="logout-btn">
          <LogOut className="w-4 h-4" style={{ color: '#7E8DA8' }} />
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#EEF1F6' }}>
      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 p-4 pt-5" style={{ background: '#0F1B33' }}>
        <SidebarNav onNav={() => {}} />
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60] md:hidden" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 z-[70] md:hidden p-4 pt-5 flex flex-col" style={{ background: '#0F1B33', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
            <SidebarNav onNav={() => setSidebarOpen(false)} />
          </aside>
        </>
      )}

      {/* Main area */}
      <main className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <TrialBanner />
        {/* Topbar */}
        <header className="shrink-0 bg-white flex items-center gap-4 px-4 sm:px-7" style={{ height: '72px', borderBottom: '1px solid #E4E8EF' }}>
          <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1.5 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5" style={{ color: '#46546E' }} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[19px] font-extrabold" style={{ color: '#0E1726', letterSpacing: '-0.3px' }}>{pageTitle}</div>
            <div className="text-[12.5px] font-medium hidden sm:block" style={{ color: '#7686A0' }}>{pageSub}</div>
          </div>
          <div className="hidden lg:flex items-center gap-2.5 rounded-[11px] px-3.5 py-2.5 w-[300px]"
            style={{ background: '#F2F4F8', border: '1px solid #E4E8EF' }}>
            <Search className="w-[17px] h-[17px]" style={{ color: '#8593AB' }} />
            <input placeholder={t('search_placeholder')} className="flex-1 bg-transparent border-none text-[13.5px] outline-none" style={{ color: '#0E1726' }} />
          </div>
          <LanguageSwitcher variant="light" />
          <button className="relative w-[42px] h-[42px] rounded-[11px] flex items-center justify-center hover:bg-gray-50"
            style={{ border: '1px solid #E4E8EF' }}>
            <Bell className="w-[18px] h-[18px]" style={{ color: '#46546E' }} />
            <span className="absolute top-[9px] right-[10px] w-2 h-2 rounded-full border-2 border-white" style={{ background: '#DC2626' }} />
          </button>
          <button onClick={() => navigate('/tickets')}
            className="hidden sm:flex items-center gap-2 rounded-[11px] px-4 py-2.5 text-[13.5px] font-bold text-white hover:opacity-90 transition-opacity"
            style={{ background: '#2A52C4', boxShadow: '0 6px 16px rgba(42,82,196,.32)' }}>
            <Plus className="w-[17px] h-[17px]" strokeWidth={2.4} />
            {t('new_ticket')}
          </button>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '26px 28px 100px' }}>
          {children}
        </div>
      </main>

      {/* Bottom nav - mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white" style={{ borderTop: '1px solid #E4E8EF', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-stretch">
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path}
                className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] transition-colors"
                style={{ color: active ? '#2A52C4' : '#8593AB' }}>
                <div className={`p-1 rounded-lg ${active ? 'bg-blue-50' : ''}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-semibold">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
